import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth0, User } from '@auth0/auth0-react';
import { SignJWT } from 'jose';

import {
  AGENCY_ID,
  AGENCY_NAME,
  AUTH_CATEGORY_QUERY,
  AUTH_ENABLED,
  CRM_ISSUE_ID,
  CRM_TOKEN,
  ELEVIO_PORTAL_URL,
  LOGIN_REQUIRED,
  PORTAL_RETURN_URL,
  PRIVATE_APP_TOKEN,
  PrivateSubmissionAppRoutes,
} from '@private-crm-app/constants';
import {
  clearPortalSessionHandoffState,
  markPortalSessionHandoffPending,
  redirectToCitizenPortalIfNeeded,
} from '@private-crm-app/portalSessionHandoff';
import { syncCrmTokenSession } from '@private-crm-app/syncCrmTokenSession';
import {
  buildIssueUrl,
  getCRMToken,
  getPrivateAppToken,
  getStoredSubmission,
} from '@private-crm-app/utils';

export interface AuthManagerContextValue {
  auth0User: User;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthResolved: boolean;
  hasAuth0Session: boolean;
  login: (screen: AuthScreen, options?: { connection?: string }) => void;
  loginWithPhone: (screen: AuthScreen) => void;
  loginWithApple: (screen: AuthScreen, appleConnectionName?: string) => void;
  loginWithGoogle: (screen: AuthScreen) => void;
  signup: (screen: AuthScreen) => void;
  signupWithPhone: (screen: AuthScreen) => void;
  logout: () => Promise<void>;
  isValidatinQueryParams: boolean;
  triggerUserRefresh: () => void;
  refreshTrigger: number;
  triggerLoginChoice: () => void;
  triggerSignupChoice: () => void;
}

/**
 * Auth0 only sets `phone_number` when the connection supplies it. SMS citizens
 * always have the number as their profile name, so fall back to that when it
 * looks like an E.164 value.
 */
function citizenPhoneNumber(auth0User: User): string {
  const phoneNumber = auth0User?.phone_number?.trim();
  if (phoneNumber) {
    return phoneNumber;
  }

  const name = auth0User?.name?.trim() || '';

  return /^\+\d{6,}$/.test(name) ? name : '';
}

export enum AuthScreen {
  CREATE_SUBMISSION = 'create-submission',
  LISTING = 'listing',
  CREATE_SUBMISSION_WITHOUT_ANONYMOUS = 'create-submission-without-anonymous',
  ISSUE = 'issue',
}

export const defaultAuthManagerValue: AuthManagerContextValue = {
  auth0User: null,
  isAuthenticated: false,
  isLoading: false,
  isAuthResolved: false,
  hasAuth0Session: false,
  login: async () => null,
  loginWithPhone: async () => null,
  loginWithApple: async () => null,
  loginWithGoogle: async () => null,
  signup: async () => null,
  signupWithPhone: async () => null,
  logout: async () => null,
  isValidatinQueryParams: false,
  triggerUserRefresh: () => undefined,
  refreshTrigger: 0,
  triggerLoginChoice: () => undefined,
  triggerSignupChoice: () => undefined,
};

type AuthManagerContextProviderProps = {
  children: React.ReactNode;
};

export const AuthManagerContext = createContext<AuthManagerContextValue>(defaultAuthManagerValue);

const validateQueryParamsOnRedirect = async (queryParams: URLSearchParams) => {
  // Local-only preview: skip CRM token validation so the modal can be inspected without
  // an Auth0/CRM-token setup. Gated by NODE_ENV so it can never weaken auth in production.
  const isDevPreview = queryParams.get('devPreview') === '1' && process.env.NODE_ENV !== 'production';
  if (isDevPreview) {
    const previewAgencyId = queryParams.get('agencyId');
    if (previewAgencyId) {
      localStorage.setItem(AGENCY_ID, previewAgencyId);
    }
    return true;
  }

  const crmToken = queryParams.get(CRM_TOKEN);
  const authEnabled = queryParams.get(AUTH_ENABLED);
  const elevioPortalUrl = queryParams.get(ELEVIO_PORTAL_URL);
  const portalReturnUrl = queryParams.get(PORTAL_RETURN_URL);
  const crmIssueId = queryParams.get(CRM_ISSUE_ID);

  // Written before any await: a citizen who already has an Auth0 session resolves
  // authentication immediately, and the handoff check would otherwise see no markers.
  if (portalReturnUrl) {
    markPortalSessionHandoffPending(portalReturnUrl);
  }

  // Always re-validate when crm_token is present so agency_name stays canonical for
  // returning sessions with the same token (previously skipped and kept stale values).
  if (crmToken) {
    try {
      const result = await syncCrmTokenSession(crmToken);

      if (!result.ok) {
        clearPortalSessionHandoffState();
        return false;
      }

      // Switching agency/token: clear any previously stored portal URL so a stale
      // agency's URL is never used as the logout redirect for this agency.
      // If the new URL is present in the query params it will be written below.
      if (result.switchedToken && !elevioPortalUrl) {
        localStorage.removeItem(ELEVIO_PORTAL_URL);
      }
      if (result.switchedToken && !portalReturnUrl) {
        clearPortalSessionHandoffState();
      }
    } catch (error) {
      console.error('error', error);
      clearPortalSessionHandoffState();
      return false;
    }
  }

  if (authEnabled) {
    localStorage.setItem(AUTH_ENABLED, authEnabled);
  }

  if (elevioPortalUrl) {
    localStorage.setItem(ELEVIO_PORTAL_URL, elevioPortalUrl);
  }

  if (crmIssueId) {
    localStorage.setItem(CRM_ISSUE_ID, crmIssueId);
  }

  return true;
};

const rememberCategoryQuery = () => {
  if (window.location.pathname !== PrivateSubmissionAppRoutes.CREATE) {
    sessionStorage.removeItem(AUTH_CATEGORY_QUERY);

    return;
  }

  const category = new URLSearchParams(window.location.search).get('category');
  if (category?.trim()) {
    sessionStorage.setItem(AUTH_CATEGORY_QUERY, category);
  } else {
    sessionStorage.removeItem(AUTH_CATEGORY_QUERY);
  }
};

// Helper function to get the route based on screen parameter
const getRouteFromScreen = (screen: string): string => {
  switch (screen) {
    case AuthScreen.LISTING:
      return PrivateSubmissionAppRoutes.LISTING;
    case AuthScreen.CREATE_SUBMISSION:
    case AuthScreen.CREATE_SUBMISSION_WITHOUT_ANONYMOUS:
      return PrivateSubmissionAppRoutes.CREATE;
    case AuthScreen.ISSUE: {
      const submission = getStoredSubmission();
      const issueId = submission?.id ?? localStorage.getItem(CRM_ISSUE_ID);
      return issueId
        ? buildIssueUrl(issueId, getCRMToken())
        : PrivateSubmissionAppRoutes.ISSUE_BASE_PATH;
    }
    default:
      return PrivateSubmissionAppRoutes.LISTING;
  }
};

export function AuthManagerContextProvider({ children }: AuthManagerContextProviderProps): JSX.Element {
  const {
    loginWithRedirect,
    isLoading: isLoadingUser,
    user: auth0User,
    logout: logoutAuth0,
    isAuthenticated,
  } = useAuth0();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [isValidatinQueryParams, setIsValidatingQueryParams] = useState(true);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const existingPath = localStorage.getItem('auth_original_path');
    if (!existingPath || currentPath === '/private-submission/create') {
      if (currentPath.startsWith('/private-submission/')) {
        localStorage.setItem('auth_original_path', currentPath);
      }
    }
  }, []);

  useEffect(() => {
    validateQueryParamsOnRedirect(queryParams)
      .then((isValid) => {
        if (!isValid) {
          window.location.href = `${process.env.APP_HOST_URL}${PrivateSubmissionAppRoutes.ERROR}`;
        }
      })
      .finally(() => {
        setIsValidatingQueryParams(false);
      });
  }, []);

  const authCode = queryParams.get('code');
  const [isLoading, setIsLoading] = useState(!!authCode);
  const [user, setUser] = useState<User>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isAuthResolved, setIsAuthResolved] = useState(false);

  const triggerUserRefresh = useCallback(() => {
    console.log('[SMS Login] Triggering user data refresh without page reload');
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const login = useCallback((screen: AuthScreen, options?: { connection?: string }) => {
    setIsLoading(true);
    rememberCategoryQuery();
    const crmToken = getCRMToken();

    const currentPath = window.location.pathname;
    if (currentPath !== '/private-submission/create') {
      localStorage.setItem('auth_intended_screen', screen);
    }

    if (currentPath.startsWith('/private-submission/')) {
      localStorage.setItem('auth_original_path', currentPath);
    }

    const authParams: any = {
      crmToken,
      apiURL: process.env.API_URL,
      comcate_app_screen: currentPath === '/private-submission/create' ? AuthScreen.CREATE_SUBMISSION : screen,
      screen_hint: 'login',
    };

    if (options?.connection) {
      authParams.connection = options.connection;
    }

    loginWithRedirect({
      authorizationParams: authParams,
    });
  }, [loginWithRedirect]);

  const loginWithPhone = useCallback((screen: AuthScreen) => {
    localStorage.setItem('auth0_login_connection', 'sms');
    localStorage.setItem('auth0_sms_reload_pending', 'true');
    localStorage.setItem('auth0_sms_login_timestamp', Date.now().toString());
    login(screen, { connection: 'sms' });
  }, [login]);

  // Apple handles both new and returning users natively (single OAuth flow).
  const loginWithApple = useCallback((screen: AuthScreen, appleConnectionName?: string) => {
    const normalizedConnectionName = appleConnectionName?.trim();
    if (!normalizedConnectionName) {
      return;
    }

    login(screen, { connection: normalizedConnectionName });
  }, [login]);

  const loginWithGoogle = useCallback((screen: AuthScreen) => {
    login(screen, { connection: 'google-oauth2' });
  }, [login]);

  const startSignupRedirect = useCallback((screen: AuthScreen, options?: { connection?: string }) => {
    setIsLoading(true);
    rememberCategoryQuery();
    const crmToken = getCRMToken();

    const currentPath = window.location.pathname;
    if (currentPath !== '/private-submission/create') {
      localStorage.setItem('auth_intended_screen', screen);
    }

    if (currentPath.startsWith('/private-submission/')) {
      localStorage.setItem('auth_original_path', currentPath);
    }

    const authParams: any = {
      crmToken,
      apiURL: process.env.API_URL,
      comcate_app_screen: currentPath === '/private-submission/create' ? AuthScreen.CREATE_SUBMISSION : screen,
      screen_hint: 'signup',
      mode: 'signUp',
    };

    if (options?.connection) {
      authParams.connection = options.connection;
    }

    loginWithRedirect({
      authorizationParams: authParams,
    });
  }, [loginWithRedirect]);

  const signup = useCallback((screen: AuthScreen) => {
    startSignupRedirect(screen);
  }, [startSignupRedirect]);

  const signupWithPhone = useCallback((screen: AuthScreen) => {
    localStorage.setItem('auth0_login_connection', 'sms');
    localStorage.setItem('auth0_sms_reload_pending', 'true');
    localStorage.setItem('auth0_sms_login_timestamp', Date.now().toString());
    startSignupRedirect(screen, { connection: 'sms' });
  }, [startSignupRedirect]);

  const logout = useCallback(async () => {
    localStorage.removeItem(CRM_TOKEN);
    localStorage.removeItem(AUTH_ENABLED);
    localStorage.removeItem(PRIVATE_APP_TOKEN);
    localStorage.removeItem(AGENCY_ID);
    localStorage.removeItem(AGENCY_NAME);
    localStorage.removeItem(CRM_ISSUE_ID);
    localStorage.removeItem('auth0_sms_reload_pending');
    localStorage.removeItem('auth0_sms_login_timestamp');
    localStorage.removeItem('auth0_login_connection');
    clearPortalSessionHandoffState();
    await logoutAuth0({
      logoutParams: {
        returnTo: `${process.env.APP_HOST_URL}/private-submission/logout-handler`,
      },
    });
  }, [logoutAuth0]);

  const triggerLoginChoice = useCallback(() => {
    localStorage.setItem('trigger_login_choice', 'true');
    localStorage.removeItem('trigger_login_choice_mode');
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const triggerSignupChoice = useCallback(() => {
    localStorage.setItem('trigger_login_choice', 'true');
    localStorage.setItem('trigger_login_choice_mode', 'signup');
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (isAuthenticated && auth0User) {
      try {
        // Defensive logout for email change completion: if the emailChangeVerification marker is present,
        // immediately log out to avoid any onboarding or protected route evaluation with a stale session.
        const emailChangeVerification =
          localStorage.getItem('emailChangeVerification') === 'true' ||
          sessionStorage.getItem('emailChangeVerification') === 'true';
        if (emailChangeVerification) {
          console.warn('[Auth][DefensiveLogout] emailChangeVerification flag detected. Logging out immediately.');
          // Clean markers before logout to avoid loops
          localStorage.removeItem('emailChangeVerification');
          sessionStorage.removeItem('emailChangeVerification');
          // Also clear generic success markers to prevent misrouting
          localStorage.removeItem('emailVerificationSuccess');
          sessionStorage.removeItem('emailVerificationSuccess');
          // Kick off logout and return early
          void logout();
          return;
        }
      } catch (e) {
        console.error('[Auth][DefensiveLogout] Failed to evaluate/logout for emailChangeVerification', e);
      }

      console.log('Auth0 user authenticated:', auth0User.email);

      const secretKey = new TextEncoder().encode(process.env.JWT_SECRET || '');

      new SignJWT({
        crmToken: getCRMToken() || '',
        email: auth0User.email || '',
        auth0UserId: auth0User.sub,
        phone: citizenPhoneNumber(auth0User),
      })
        .setProtectedHeader({ alg: 'HS256' })
        .sign(secretKey)
        .then(async (token) => {
          localStorage.setItem(PRIVATE_APP_TOKEN, token);
          setUser(auth0User);
          setIsLoading(false);
          setIsAuthResolved(true);

          const urlParams = new URLSearchParams(window.location.search);
          const verifyToken = urlParams.get('verifyToken');
          const loginRequired = urlParams.get(LOGIN_REQUIRED) === 'true';

          if (!verifyToken && !loginRequired) {
            const handoffRedirected = await redirectToCitizenPortalIfNeeded(
              token,
              window.location.search,
              window.location.pathname
            );
            if (handoffRedirected) {
              return;
            }
          }

          // Check for SMS login and trigger immediate refresh (no delays needed)
          const needsSMSReload = localStorage.getItem('auth0_sms_reload_pending') === 'true';
          const wasConnectionSMS = localStorage.getItem('auth0_login_connection') === 'sms';
          const isCurrentlySMSUser = auth0User?.sub?.startsWith('sms|');
          const userIdentifier = auth0User?.email || auth0User?.sub;
          const smsLinkingCompleteKey = userIdentifier ? `auth0_sms_linking_complete_${userIdentifier}` : null;
          const hasCompletedSMSLinking = smsLinkingCompleteKey
            ? localStorage.getItem(smsLinkingCompleteKey) === 'true'
            : false;
          // Only trigger SMS refresh for active SMS users, not users with historical SMS identities
          // This prevents conflicts with email verification flows for users who previously linked SMS
          const isPotentialFirstTimeSMS = isCurrentlySMSUser && !hasCompletedSMSLinking;

          if ((needsSMSReload || wasConnectionSMS || isPotentialFirstTimeSMS) && userIdentifier) {
            console.log('[SMS Login] SMS login detected, no waiting needed - using phone number lookup API');

            // Mark that this user has completed SMS linking to avoid future reloads
            if (smsLinkingCompleteKey) {
              localStorage.setItem(smsLinkingCompleteKey, 'true');
            }

            // Clean up all SMS-related flags
            localStorage.removeItem('auth0_sms_reload_pending');
            localStorage.removeItem('auth0_sms_login_timestamp');
            localStorage.removeItem('auth0_login_connection');

            // Trigger immediate user data refresh (no delay needed with phone number lookup API)
            console.log('[SMS Login] Triggering immediate user data refresh using phone number lookup');
            triggerUserRefresh();
          }

          const storedScreen = localStorage.getItem('auth_intended_screen');
          const screenParam = storedScreen || urlParams.get('comcate_app_screen');
          const originalPath = localStorage.getItem('auth_original_path');

          // Redirect if there are stored values indicating we came from a login flow or if there is a verifyToken
          const shouldRedirect = storedScreen || screenParam || originalPath || verifyToken;

          if (!shouldRedirect) {
            // User is already authenticated and navigating directly - no redirect needed
            return;
          }

          let targetRoute;
          if (verifyToken) {
            // Check if we're already on the profile page with verifyToken
            const currentPath = window.location.pathname;
            const hasVerifiedEmail =
              sessionStorage.getItem('emailVerificationSuccess') === 'true' ||
              localStorage.getItem('emailVerificationSuccess') === 'true';

            if (currentPath === PrivateSubmissionAppRoutes.PROFILE) {
              // We're already on the profile page, don't redirect - let UserProfile handle verification
              console.log('Already on profile page with verifyToken, allowing UserProfile to handle verification');
              return;
            }

            if (hasVerifiedEmail) {
              // Email has been verified, don't redirect
              console.log('Email already verified, no redirect needed');
              return;
            }

            targetRoute = PrivateSubmissionAppRoutes.PROFILE;
          } else if (originalPath === '/private-submission/create') {
            targetRoute = originalPath;
          } else if (
            originalPath &&
            originalPath.startsWith(`${PrivateSubmissionAppRoutes.ISSUE_BASE_PATH}/`)
          ) {
            targetRoute = originalPath;
          } else if (screenParam) {
            targetRoute = getRouteFromScreen(screenParam);
          } else if (originalPath && originalPath.startsWith('/private-submission/')) {
            targetRoute = originalPath;
          } else {
            targetRoute = PrivateSubmissionAppRoutes.LISTING;
          }

          const currentPath = window.location.pathname;

          const stashedCategory = sessionStorage.getItem(AUTH_CATEGORY_QUERY);

          // Clean up stored values
          localStorage.removeItem('auth_intended_screen');
          localStorage.removeItem('auth_original_path');
          sessionStorage.removeItem(AUTH_CATEGORY_QUERY);

          // Only redirect if we're not already on the target route
          if (currentPath !== targetRoute) {
            const crmToken = urlParams.get('crm_token');
            // Read both camel and snake case, prefer what came in
            const authEnabled = urlParams.get(AUTH_ENABLED) || urlParams.get('authEnabled');

            let redirectUrl = `${process.env.APP_HOST_URL}${targetRoute}`;
            const redirectParams = new URLSearchParams();

            if (crmToken) {
              redirectParams.append('crm_token', crmToken);
            }
            if (authEnabled) {
              // Preserve original snake_case expected by downstream code
              redirectParams.append(AUTH_ENABLED, authEnabled);
            }
            if (verifyToken) {
              redirectParams.append('verifyToken', verifyToken);
            }
            if (targetRoute === PrivateSubmissionAppRoutes.CREATE && stashedCategory?.trim()) {
              redirectParams.append('category', stashedCategory);
            }

            if (redirectParams.toString()) {
              redirectUrl += `?${redirectParams.toString()}`;
            }

            window.location.href = redirectUrl;
            return;
          }
        })
        .catch((error) => {
          console.error('Error signing JWT:', error);
          setUser(auth0User);
          setIsLoading(false);
          setIsAuthResolved(true);
        });
    } else if (!isLoadingUser && !auth0User) {
      setUser(null);
      setIsLoading(false);
      setIsAuthResolved(true);
    }
  }, [auth0User, isAuthenticated, isLoadingUser]);

  // Safety net for the portal handoff: the inline attempt above runs inside the
  // Auth0 callback path and can fire before portal markers are stored or before a
  // returning citizen's token is available. Re-check once both have settled.
  useEffect(() => {
    if (isValidatinQueryParams || !user) {
      return;
    }

    void redirectToCitizenPortalIfNeeded(getPrivateAppToken());
  }, [user, isValidatinQueryParams, isAuthResolved]);

  const value = useMemo<AuthManagerContextValue>(
    () => ({
      auth0User: user,
      isAuthenticated: !!user,
      // Keep loading true while Auth0 has restored a session (auth0User present)
      // but our app-level user has not been set yet, to avoid flashing the login-choice modal
      isLoading: isLoading || isLoadingUser || (!!auth0User && !user),
      isAuthResolved,
      hasAuth0Session: isAuthenticated,
      login,
      loginWithPhone,
      loginWithApple,
      loginWithGoogle,
      signup,
      signupWithPhone,
      logout,
      isValidatinQueryParams,
      triggerUserRefresh,
      refreshTrigger,
      triggerLoginChoice,
      triggerSignupChoice,
    }),
    [
      user,
      isLoading,
      isLoadingUser,
      isAuthResolved,
      login,
      loginWithPhone,
      loginWithApple,
      loginWithGoogle,
      signup,
      signupWithPhone,
      logout,
      isValidatinQueryParams,
      triggerUserRefresh,
      refreshTrigger,
      triggerLoginChoice,
      triggerSignupChoice,
    ]
  );

  return <AuthManagerContext.Provider value={value}>{children}</AuthManagerContext.Provider>;
}

export function useAuthManagerContext(): AuthManagerContextValue {
  const context = useContext(AuthManagerContext);
  if (!context) {
    throw Error('Component is not inside AuthManagerContext Provider');
  }

  return context;
}
